import { UserPermission } from '@globaleris/ui.library'
import { Box, CardProps, Chip } from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import { HttpErrorProps } from '../../../hooks'
import { ObjectionTableModel } from '../../../pages/invoicesPage/invoicesPage.Model'
import { ObjectionsDataTable } from '../objectionsDataTable/objectionsDataTable'
import { StandardCard } from '../standardCard/standardCard'

//Codigos de objecciones que deben ir primero en la lista de chips
const CHIP_PRIORITY = ['LEAD_ERROR_TECNICO', 'LEAD_CONTEXTO', 'LEAD_DEPENDENCIA']

interface ObjectionsCardProps extends CardProps {
  title?: string
  hideTitle?: boolean
  isLoading?: boolean
  show?: boolean
  objDescriptions: ObjectionTableModel[]
  objectionsData?: ObjectionTableModel[]
  showDescription?: boolean // usar campo descripcion para objeciones maestras
  showState?: boolean
  showIdObjMaestra?: boolean
  taskDomain?: EnumDominioTarea // dominio para rellenar combo añadir objecion
  errorProps?: HttpErrorProps & { title?: string }
  allowInsert?: boolean
  hasEditPermission?: boolean
  tableUserPermissions?: UserPermission[]
  groupByCode?: boolean
  onRowDelete?: (row: ObjectionTableModel) => Promise<void>
  onRowAdd?: (row: ObjectionTableModel) => Promise<void>
  onTerminateObjection?: (row: ObjectionTableModel) => Promise<void>
}

export const ObjectionsCard: React.FC<ObjectionsCardProps> = ({
  isLoading = false,
  show = true,
  objDescriptions,
  objectionsData,
  showDescription = false,
  showState = false,
  showIdObjMaestra = false,
  taskDomain,
  errorProps,
  allowInsert = false,
  hasEditPermission = false,
  tableUserPermissions,
  groupByCode = false,
  onRowDelete,
  onRowAdd,
  onTerminateObjection,
  title,
  hideTitle = false,
  ...props
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'objectionsCard'])

  const chips = useMemo(() => {
    if (!groupByCode || !objectionsData) return []
    const uniqueCodes = [...new Set(objectionsData.map(o => o.code).filter(Boolean))] as string[]
    const priorityCodes = CHIP_PRIORITY.filter(c => uniqueCodes.includes(c))
    const otherCodes = uniqueCodes.filter(c => !CHIP_PRIORITY.includes(c))
    const sortedCodes = [...priorityCodes, ...otherCodes]
    return sortedCodes.map(code => ({
      code,
      description: objectionsData.find(o => o.code === code)!.description
    }))
  }, [objectionsData, groupByCode])

  const [selectedCode, setSelectedCode] = useState<string | null>(() => chips[0]?.code ?? null)

  useEffect(() => {
    setSelectedCode(chips[0]?.code ?? null)
  }, [chips])

  const noData = objectionsData && objectionsData.length === 0

  const filteredObjections = useMemo(() => {
    if (!groupByCode || !selectedCode || !objectionsData) return objectionsData
    return objectionsData.filter(o => o.code === selectedCode)
  }, [objectionsData, selectedCode, groupByCode])

  const handleChipClick = (code: string) => {
    setSelectedCode(code)
  }

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return show ? (
    <StandardCard
      isLoading={isLoading}
      noTransition={false}
      showEmptyContent={noData}
      emptyCaption={t('objectionsCard:emptyCaption')}
      {...props}
    >
      {
        <Box>
          {chips.length > 0 && (
            <Box
              display="flex"
              justifyContent="flex-end"
              flexWrap="wrap"
              style={{ gap: '4px', marginTop: '8px' }}
            >
              {chips.map(chip => (
                <Chip
                  key={chip.code}
                  label={chip.description}
                  size="small"
                  clickable
                  color={selectedCode === chip.code ? 'primary' : 'default'}
                  onClick={() => handleChipClick(chip.code)}
                  style={{ fontSize: '0.72rem', height: '24px' }}
                />
              ))}
            </Box>
          )}
        </Box>
      }
      <ObjectionsDataTable
        title={!hideTitle ? (title ?? t('objectionsCard:title')) : undefined}
        objDescriptions={objDescriptions}
        objectionsData={filteredObjections}
        errorProps={errorProps}
        showDescription={showDescription}
        taskDomain={taskDomain}
        allowInsert={allowInsert}
        hasEditPermission={hasEditPermission}
        tableUserPermissions={tableUserPermissions}
        onRowDelete={onRowDelete}
        onRowAdd={onRowAdd}
        onTerminateObjection={onTerminateObjection}
        showState={showState}
        showIdObjMaestra={showIdObjMaestra}
      />
    </StandardCard>
  ) : (
    <></>
  )
}
